import QRCode from "qrcode";
import { displayLockerNumber } from "../../../utils/displayLockerNumber";

/** Avery 5160 / 8160: 1" x 2.625", 3 columns x 10 rows, US Letter. */
export const AVERY_COLUMNS = 3;
export const AVERY_ROWS = 10;
export const AVERY_PER_SHEET = AVERY_COLUMNS * AVERY_ROWS;

export function averySlots(labelCount, startPosition) {
  const count = Math.max(0, Number(labelCount) || 0);
  if (!count) return [];
  const start = Math.min(
    AVERY_PER_SHEET,
    Math.max(1, Number(startPosition) || 1)
  );
  const pages = [];
  let left = count;
  let offset = start - 1;
  while (left > 0) {
    const take = Math.min(AVERY_PER_SHEET - offset, left);
    pages.push({ offset, take });
    left -= take;
    offset = 0;
  }
  return pages;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function labelHtml(label) {
  if (!label) return `<div class="label"></div>`;
  const department = escapeHtml(label.department);
  const number = escapeHtml(label.number);
  return `<div class="label">
    <img src="${label.qr}" alt="QR code for locker ${number}" />
    <div class="text">
      ${department ? `<div class="dept">${department}</div>` : ""}
      <div class="num">Locker ${number}</div>
    </div>
  </div>`;
}

function sheetHtml(labels, offset, take) {
  const cells = Array(AVERY_PER_SHEET).fill("");
  for (let i = 0; i < take; i += 1) {
    cells[offset + i] = labelHtml(labels[i]);
  }
  const blank = labelHtml(null);
  return `<section class="sheet">${cells
    .map((cell) => cell || blank)
    .join("")}</section>`;
}

async function labelsFromLockers(lockers) {
  const labels = [];
  for (const locker of lockers) {
    const pin = String(locker?.pin_code || "").trim();
    if (!pin) continue;
    const qr = await QRCode.toDataURL(pin, {
      margin: 0,
      width: 180,
      errorCorrectionLevel: "M",
    });
    labels.push({
      qr,
      department: String(locker.department_name || "").trim(),
      number:
        String(locker.number || "").trim() ||
        locker.display_number ||
        displayLockerNumber(locker.number),
    });
  }
  return labels;
}

function documentHtml(labels, startPosition) {
  const pages = averySlots(labels.length, startPosition);
  let cursor = 0;
  const sheets = pages
    .map((page) => {
      const slice = labels.slice(cursor, cursor + page.take);
      cursor += page.take;
      return sheetHtml(slice, page.offset, page.take);
    })
    .join("");
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Avery labels</title>
  <style>
    @page { size: letter; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    .hint {
      font: 14px Arial, sans-serif;
      padding: 16px 24px;
      background: #f4f6fb;
    }
    .sheet {
      width: 8.5in;
      height: 11in;
      padding: 0.5in 0.1875in 0;
      box-sizing: border-box;
      display: grid;
      grid-template-columns: repeat(3, 2.625in);
      grid-template-rows: repeat(10, 1in);
      column-gap: 0.125in;
      row-gap: 0;
      page-break-after: always;
    }
    .label {
      display: flex;
      align-items: center;
      gap: 0.08in;
      overflow: hidden;
      padding: 0.06in 0.08in;
      box-sizing: border-box;
    }
    .label img { width: 0.78in; height: 0.78in; }
    .dept {
      font: 700 9pt Arial, sans-serif;
      line-height: 1.15;
      word-break: break-word;
    }
    .num { font: 8pt Arial, sans-serif; margin-top: 2px; }
    @media print { .hint { display: none; } }
  </style>
</head>
<body>
  <p class="hint">Avery 5160. In the print dialog choose Actual size (100%). Do not use Fit to page.</p>
  ${sheets}
</body>
</html>`;
}

export async function printAveryLabels(lockers, startPosition) {
  const win = window.open("", "avery-labels");
  if (!win) {
    return { ok: false, error: "Allow pop-ups to print labels." };
  }
  win.document.write(
    "<p style='font-family:Arial,sans-serif;padding:24px'>Preparing labels…</p>"
  );
  try {
    const labels = await labelsFromLockers(lockers);
    if (!labels.length) {
      win.close();
      return { ok: false, error: "A PIN has to be created before a label can be printed." };
    }
    win.document.open();
    win.document.write(documentHtml(labels, startPosition));
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
    }, 250);
    return { ok: true };
  } catch (error) {
    win.close();
    return { ok: false, error: "Could not generate the QR codes." };
  }
}
